/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@atlaskit/css';
import CheckCircleUncheckedIcon from '@atlaskit/icon/core/check-circle-unchecked';
import Image from '@atlaskit/image';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Anchor, Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { Card } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { CapacityPlanningPayload } from '@atlassian/conversation-assistant-message-actions-api/capacity-planning';

import { getWorkId } from '../capacity-planning-utils';
import type { HydratedWork } from '../types';

const styles = cssMap({
	card: {
		display: 'inline-flex',
		alignItems: 'center',
		verticalAlign: 'middle',
		transform: `translateY(${token('space.025')})`,
		/** Keep chip from collapsing when the action label is `nowrap` + long dates (people view UPDATE). */
		flexShrink: 0,
		minWidth: '0px',
		maxWidth: '100%',
		/** Ancestor `overflow: hidden` was clipping the card title; inline `Card` uses `truncateInline` for the title. */
		overflow: 'visible',
	},
	/** Plain text link for dense headers — same information as the smart card without the inline chip frame. */
	headerLinkWrap: {
		minWidth: '0px',
		maxWidth: '100%',
		flex: '1 1 0%',
		overflow: 'hidden',
	},
	/** Grows in flex row so long titles shrink and show an ellipsis. */
	headerWorkLink: {
		color: token('color.text'),
		textDecoration: 'none',
		minWidth: '0px',
		width: '100%',
		maxWidth: '100%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		display: 'block',
	},
	headerWorkAnchor: {
		color: token('color.text'),
		textDecoration: 'none',
		minWidth: '0px',
		width: '100%',
		maxWidth: '100%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		display: 'block',
		pointerEvents: 'auto',
	},
	headerWorkName: {
		display: 'block',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	headerWorkRow: {
		minWidth: '0px',
		maxWidth: '100%',
		width: '100%',
		overflow: 'hidden',
	},
	issueTypeIconWrap: {
		display: 'flex',
		flexShrink: 0,
		alignItems: 'center',
		paddingInline: token('space.075'),
		paddingBlock: token('space.050'),
	},
});

export type WorkItemFromHydratedWorkProps = {
	work: HydratedWork;
	isGroupHeader?: boolean;
};

/**
 * Renders a work item the same way as {@link WorkItemNode} for a payload, but from an already-hydrated work
 * (e.g. work-view group headers where the parent already resolved `work` from `workById`).
 */
export function WorkItemFromHydratedWork({
	work,
	isGroupHeader,
}: WorkItemFromHydratedWorkProps): React.JSX.Element | null {
	// render the work item in the group header
	if (isGroupHeader) {
		if (work.url) {
			// the jira work item has a url
			return (
				<Inline alignBlock="center" space="space.050" xcss={styles.headerWorkRow}>
					{work.issueTypeAvatarUrl ? (
						work.issueTypeName ? (
							<Tooltip content={work.issueTypeName}>
								<Box xcss={styles.issueTypeIconWrap}>
									<Image
										alt={work.issueTypeName}
										src={work.issueTypeAvatarUrl}
										srcDark={work.issueTypeAvatarUrl}
										width={16}
										height={16}
									/>
								</Box>
							</Tooltip>
						) : (
							<Box xcss={styles.issueTypeIconWrap}>
								<Image
									alt=""
									src={work.issueTypeAvatarUrl}
									srcDark={work.issueTypeAvatarUrl}
									width={16}
									height={16}
								/>
							</Box>
						)
					) : null}
					<Box xcss={styles.headerLinkWrap}>
						<Tooltip content={work.name}>
							<Anchor
								href={work.url}
								target="_blank"
								rel="noopener noreferrer"
								xcss={styles.headerWorkAnchor}
								onClick={(e) => e.stopPropagation()}
							>
								<Box as="span" xcss={styles.headerWorkName}>
									<Text size="medium" weight="bold" as="span">
										{work.name}
									</Text>
								</Box>
							</Anchor>
						</Tooltip>
					</Box>
				</Inline>
			);
		} else {
			// non project work item doesn't have the url
			return (
				<Inline alignBlock="center" space="space.050" xcss={styles.headerWorkRow}>
					<Box xcss={styles.issueTypeIconWrap}>
						<CheckCircleUncheckedIcon label="" />
					</Box>
					<Box xcss={styles.headerLinkWrap}>
						<Tooltip content={work.name}>
							<Box xcss={styles.headerWorkLink}>
								<Box as="span" xcss={styles.headerWorkName}>
									<Text size="medium" weight="bold" as="span">
										{work.name}
									</Text>
								</Box>
							</Box>
						</Tooltip>
					</Box>
				</Inline>
			);
		}
	} else {
		// render the work item in the action label
		// render the jira work item
		if (work.url) {
			return (
				<Box xcss={styles.card}>
					<SmartCardProvider>
						<Card url={work.url} appearance="inline" showHoverPreview truncateInline />
					</SmartCardProvider>
				</Box>
			);
		} else {
			// render the non project work item
			return <span>&apos;{work.name}&apos;</span>;
		}
	}
}

export type WorkItemNodeProps = {
	payload: CapacityPlanningPayload;
	workById: Map<string, HydratedWork>;
	isGroupHeader?: boolean;
};

/**
 * Resolves the work for `payload` via {@link getWorkId} and `workById`, then renders the same as
 * {@link WorkItemFromHydratedWork}.
 */
export default function WorkItemNode({
	payload,
	workById,
	isGroupHeader,
}: WorkItemNodeProps): React.JSX.Element | null {
	const workId = getWorkId(payload);
	const resolved = workId ? workById.get(workId) : undefined;
	if (!resolved) {
		return null;
	}

	return <WorkItemFromHydratedWork work={resolved} isGroupHeader={isGroupHeader} />;
}
